/**
 * Size utilities for handling product size conversions and sorting
 * This module contains all size-related logic for the Attraqt product processing
 */

import type { ProductSize, ProductVariant } from 'types/models/product'
import { isProductSize } from 'types/models/product'

/**
 * Formats raw size values from PIM into something more presentable
 * @param size string
 * @returns ProductSize
 */
export function convertSizeToAcronym(size: string): ProductSize {
  if (size.endsWith('Years'))
    size = `${size.slice(0, -6)}Y`

  if (size.endsWith('Months'))
    size = `${size.slice(0, -7)}M`

  if (!isProductSize(size)) {
    return size as ProductSize
  }

  return size
}

/**
 * Convert full size names to their acronym equivalent
 * @param size string - The full size name
 * @returns string - The size acronym
 */
export function convertFullNameToAcronym(size: string): string {
  const acronymMapping: { [key: string]: string } = {
    'extra extra extra small': 'XXXS',
    'extra extra small': 'XXS',
    'extra small': 'XS',
    'small': 'S',
    'small/medium': 'SM',
    'medium': 'M',
    'medium/large': 'ML',
    'large': 'L',
    'large/extra large': 'LXL',
    'extra large': 'XL',
    'extra extra large': 'XXL',
    'extra extra extra large': 'XXXL',
    'one size': 'ONESIZE',
  }

  const normalizedSize = size.toLowerCase()

  // Return acronym if mapping exists
  if (acronymMapping[normalizedSize]) {
    return acronymMapping[normalizedSize]
  }

  // For sizes that are already acronyms or numeric, return as-is
  return size
}

/**
 * Apply sort function to variants
 * Enhanced to support both size acronyms (XS, S, M, L, XL) and full size names (Extra Small, Small, Medium, Large, Extra Large)
 * @param previous
 * @param current
 * @returns Number
 */
export function sortVariantFunction(previous: ProductVariant, current: ProductVariant): number {
  // Size mapping supports both acronyms and full names for proper sorting
  const sizeMapping: { [key: string]: number } = {
    'xxxs': 0,
    'xxs': 1,
    'xs': 2,
    'extra small': 2,
    's': 3,
    'small': 3,
    'sm': 3,
    'small/medium': 3.5,
    'm': 4,
    'medium': 4,
    'ml': 4.5,
    'medium/large': 4.5,
    'l': 5,
    'large': 5,
    'lxl': 5.5,
    'large/extra large': 5.5,
    'xl': 6,
    'extra large': 6,
    'xxl': 7,
    'xxxl': 8,
  }

  let currentSize: string | number = current.size.toLowerCase()
  let previousSize: string | number = previous.size.toLowerCase()

  switch (true) {
    case Boolean(sizeMapping[currentSize]):
      currentSize = sizeMapping[currentSize]
      previousSize = sizeMapping[previousSize]
      break
    case currentSize.includes('m') || currentSize.includes('y'):
      currentSize = Number.parseInt(currentSize.split('-').shift() || '0')
      previousSize = Number.parseInt(previousSize.split('-').shift() || '0')

      if (current.size.toLowerCase().includes('y'))
        currentSize = currentSize * 12

      if (previous.size.toLowerCase().includes('y'))
        previousSize = previousSize * 12

      break
    default:
      currentSize = Number.parseInt(currentSize.slice(0, 2))
      previousSize = Number.parseInt(previousSize.slice(0, 2))
      break
  }

  return previousSize - currentSize
}
